// What a statement's object is, read from the property's declaration: a thing
// to pick, or a value of a kind (a date, a number, text in a language). The
// entity page asks for the object in those terms instead of "Entity or
// Literal" and a typed datatype IRI (77.7).
import type { TermInput, UriLabel } from '@/lib/useEntity';
import type { SchemaProperty } from '@/lib/useOntology';
import { CORE_ANNOTATION_PREDICATES } from '@/lib/coreLanguage';

const XSD = 'http://www.w3.org/2001/XMLSchema#';

/** The kinds of value a person can enter, each with its plain name. */
export type ValueType = 'text' | 'number' | 'whole' | 'date' | 'datetime' | 'time' | 'yesno' | 'web';

export const VALUE_TYPES: ReadonlyArray<{ type: ValueType; name: string; datatype: string | null }> = [
  { type: 'text', name: 'Text', datatype: null },
  { type: 'number', name: 'Number', datatype: `${XSD}decimal` },
  { type: 'whole', name: 'Whole number', datatype: `${XSD}integer` },
  { type: 'date', name: 'Date', datatype: `${XSD}date` },
  { type: 'datetime', name: 'Date and time', datatype: `${XSD}dateTime` },
  { type: 'time', name: 'Time of day', datatype: `${XSD}time` },
  { type: 'yesno', name: 'Yes or no', datatype: `${XSD}boolean` },
  { type: 'web', name: 'Web address', datatype: `${XSD}anyURI` },
];

/** XSD local names by the value type that enters them. */
const XSD_TYPES: Record<string, ValueType> = {
  string: 'text',
  normalizedstring: 'text',
  token: 'text',
  langstring: 'text',
  decimal: 'number',
  double: 'number',
  float: 'number',
  integer: 'whole',
  int: 'whole',
  long: 'whole',
  short: 'whole',
  byte: 'whole',
  nonnegativeinteger: 'whole',
  positiveinteger: 'whole',
  nonpositiveinteger: 'whole',
  negativeinteger: 'whole',
  unsignedint: 'whole',
  unsignedlong: 'whole',
  date: 'date',
  datetime: 'datetime',
  datetimestamp: 'datetime',
  time: 'time',
  boolean: 'yesno',
  anyuri: 'web',
};

/** What the object of a statement on this property is. */
export type ValueShape =
  | { kind: 'thing'; pick: 'class' | undefined }
  | { kind: 'value'; type: ValueType; datatype: string | null }
  /** Nothing declared: the person says whether it is a thing or a value. */
  | { kind: 'either' };

/** `:type` / `rdf:type` states what a thing IS; its object is a class, and the
 *  statement carries no tags (ruled 2026-08-03). */
const TYPE_PREDICATES = new Set([':type', 'http://www.w3.org/1999/02/22-rdf-syntax-ns#type']);

export function isTypePredicate(uri: string): boolean {
  return TYPE_PREDICATES.has(uri);
}

function xsdLocal(range: string): string | null {
  const m = /^(?:xsd:|https?:\/\/www\.w3\.org\/2001\/XMLSchema#)(.+)$/i.exec(range.trim());
  return m?.[1] ?? null;
}

/** Reads the shape from the declaration. A declared XSD range is the value
 *  kind it names, kept as declared so the statement matches the ontology;
 *  an object property, or a range that is a class, is a thing. */
export function valueShape(predicate: string, declared: SchemaProperty | undefined): ValueShape {
  if (isTypePredicate(predicate)) return { kind: 'thing', pick: 'class' };
  if (CORE_ANNOTATION_PREDICATES.some((c) => c.uri === predicate)) {
    return { kind: 'value', type: 'text', datatype: null };
  }
  if (!declared) return { kind: 'either' };
  const range = declared.range[0];
  if (declared.kind === 'object') return { kind: 'thing', pick: undefined };
  if (range) {
    const local = xsdLocal(range);
    const type = local ? XSD_TYPES[local.toLowerCase()] : undefined;
    if (type) return { kind: 'value', type, datatype: type === 'text' ? null : `${XSD}${local}` };
    if (!local) return { kind: 'thing', pick: undefined };
  }
  return declared.kind === 'datatype' ? { kind: 'value', type: 'text', datatype: null } : { kind: 'either' };
}

/** Object-term editor state: a thing picked, or a value entered. */
export type ObjState =
  | { mode: 'uri'; sel: UriLabel | null }
  | { mode: 'literal'; value: string; lang: string; datatype: string };

export const EMPTY_THING: ObjState = { mode: 'uri', sel: null };
export const EMPTY_LITERAL: Extract<ObjState, { mode: 'literal' }> = { mode: 'literal', value: '', lang: '', datatype: '' };

/** The browser's date-time and time boxes stop at minutes; the stored lexical
 *  form carries seconds. */
export function lexicalFor(datatype: string, raw: string): string {
  if (datatype.endsWith('#dateTime') && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) return `${raw}:00`;
  if (datatype.endsWith('#time') && /^\d{2}:\d{2}$/.test(raw)) return `${raw}:00`;
  return raw;
}

export function objStateToTerm(s: ObjState): TermInput | null {
  if (s.mode === 'uri') return s.sel ? { uri: s.sel.uri } : null;
  if (!s.value.trim()) return null;
  return {
    value: lexicalFor(s.datatype, s.value.trim()),
    lang: s.lang.trim() || undefined,
    datatype: s.datatype.trim() || undefined,
  };
}
